import React, { ReactElement, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { compose } from 'redux';
import { parse, stringify } from 'b2b-apps/shop/common/utils/queryParams';
import { useFetchNewsroomNews, useNewsroomNewsData } from 'b2b-apps/shop/content/hooks';
import { useActiveLanguage } from 'b2b-apps/shop/i18n/hooks';
import { useRouterPush } from 'b2b-apps/shop/routes/hooks';
import { openLink } from 'b2b-apps/shop/routes/utils/url';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { location } = props;
    const push = useRouterPush();
    const fetchNews = useFetchNewsroomNews();
    const { data } = useNewsroomNewsData();
    const currentPage = +parse(location.search).page;
    const lang = useActiveLanguage();

    useEffect(() => {
      fetchNews(currentPage);
    }, [fetchNews, currentPage]);

    const handlePaginationPageChange = useCallback(
      (nextPage: number): void => {
        const newPageParam = {
          page: nextPage,
        };

        push({
          search: stringify(newPageParam),
        });
      },
      [push],
    );

    const handleNewsButtonClick = useCallback(
      (url: string): void => {
        openLink(url, push);
      },
      [push],
    );

    const injectedProps: InjectedProps = {
      newsData: data,
      lang,
      onPaginationPageChange: handlePaginationPageChange,
      onNewsButtonClick: handleNewsButtonClick,
      page: currentPage,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return compose<EnhancedComponentType>(
    withRouter,
  )(
    React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps)),
  );
};

export { withContainer };
